// Virtual net billing: the supplies to be netted, on the consumption page.
//
// Each supply has a name, its allocation percentage from the contract appendix
// and, with two or more supplies, its own hourly consumption file. The
// project's consumption is then the hourly sum of those files: it is shown
// below them, read-only, and set automatically (alignVirtualNetBillingLoad).
// With one supply the project's own consumption inputs remain in use.
import { useState } from "react";
import { Alert, Box, Button, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MeteredProfileImport from "./MeteredProfileImport";
import MaybeTooltip from "./MaybeTooltip";
import ManualLink from "./ManualLink";
import VirtualNetBillingAllocation from "./VirtualNetBillingAllocation";
import { useTranslateText } from "../context/LanguageContext";
import {
  DEFAULT_LOSS_UPLIFT_LV_PCT,
  DEFAULT_LOSS_UPLIFT_MV_PCT,
  VNB_ADD_SUPPLY,
  VNB_AGREED_POWER_NOTE,
  VNB_ASSUMPTIONS_NOTE,
  VNB_ASSUMPTIONS_NOTE_VOLTAGE,
  VNB_LOSS_HELPER,
  VNB_LOSS_LV_LABEL,
  VNB_LOSS_MV_LABEL,
  VNB_REDUCTION_FACTOR_LABEL,
  VNB_REMOVE_SUPPLY,
  VNB_STATION_VOLTAGE_LABEL,
  VNB_SUPPLY_VOLTAGE_LABEL,
  VNB_VOLTAGE_OPTIONS,
  VNB_VOLTAGE_SAME_OPTION,
  VNB_VOLTAGE_SURPLUS_NOTE,
  VNB_VOLTAGE_TEXT,
  VNB_VOLTAGE_TITLE,
  VNB_SHARES_TOTAL_LABEL,
  VNB_SINGLE_SUPPLY_NOTE,
  VNB_SUM_LABEL,
  VNB_SUM_LOCKED_NOTE,
  VNB_SUM_PENDING_NOTE,
  VNB_SUM_SOURCE,
  VNB_SUPPLIES_TEXT,
  VNB_SUPPLIES_TITLE,
  VNB_SUPPLY_AGREED_LABEL,
  VNB_OPTIONAL_PLACEHOLDER,
  VNB_SUPPLY_FILE_TITLE,
  VNB_SUPPLY_NAME_LABEL,
  VNB_SUPPLY_SHARE_LABEL,
  alignVirtualNetBillingLoad,
  newSupplyId,
  validateVirtualNetBilling,
  voltageLevels,
} from "./config/virtualNetBilling";

const numberOrBlank = (value) => (value === "" ? "" : Number(value));

/** cfg: the project config; onChange(nextCfg) commits the whole config. */
export default function VirtualNetBillingSupplies({ cfg, onChange }) {
  const btm = cfg?.btm || {};
  const vnb = btm.virtualNetBilling || {};
  const supplies = Array.isArray(vnb.supplies) ? vnb.supplies : [];
  const validation = validateVirtualNetBilling(btm, cfg);
  // The file caption is not translated in place (it carries numbers).
  const translateText = useTranslateText();
  const [sharesTouched, setSharesTouched] = useState(false);

  const commitSupplies = (nextSupplies) => {
    const next = { ...cfg, btm: { ...btm, virtualNetBilling: { ...vnb, supplies: nextSupplies } } };
    onChange(alignVirtualNetBillingLoad(next));
  };
  const patchVnb = (partial) =>
    onChange({ ...cfg, btm: { ...btm, virtualNetBilling: { ...vnb, ...partial } } });
  const voltage = voltageLevels(vnb);
  const patchSupply = (index, partial) =>
    commitSupplies(supplies.map((s, i) => (i === index ? { ...s, ...partial } : s)));
  const addSupply = () =>
    commitSupplies([...supplies, { id: newSupplyId(), name: String(supplies.length + 1), sharePct: "" }]);
  const removeSupply = (index) => commitSupplies(supplies.filter((s, i) => i !== index));

  const sharesTotal = supplies.reduce((total, s) => {
    const n = Number(String(s?.sharePct ?? "").replace(",", "."));
    return Number.isFinite(n) ? total + n : total;
  }, 0);
  const several = supplies.length >= 2;
  const projectLoad = cfg?.feasibility?.technical?.demand?.loadProfile;
  const sumInUse = several && projectLoad?.source === VNB_SUM_SOURCE && Boolean(projectLoad?.values?.length);
  const fileErrors = ["vnbFiles", "vnbSum"].map((key) => validation[key]).filter(Boolean);

  return (
    <Box id="technical-virtual-net-billing" sx={{ p: 1.5, border: "1px solid", borderColor: "success.main", borderRadius: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {VNB_SUPPLIES_TITLE}
        </Typography>
        <ManualLink settlementType={cfg?.settlementType} section="vnbSupplies" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {VNB_SUPPLIES_TEXT}
      </Typography>

      {supplies.map((supply, index) => (
        <Box key={supply.id || index} sx={{ mb: 1.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: voltage.station ? "2fr 1fr 1fr 1.4fr auto" : "2fr 1fr 1fr auto" }, gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small"
              label={VNB_SUPPLY_NAME_LABEL}
              value={supply.name ?? ""}
              onChange={(e) => patchSupply(index, { name: e.target.value })}
              inputProps={{ "data-no-translate": "" }}
            />
            <TextField
              size="small"
              label={VNB_SUPPLY_SHARE_LABEL}
              type="number"
              value={supply.sharePct ?? ""}
              onChange={(e) => {
                setSharesTouched(true);
                patchSupply(index, { sharePct: numberOrBlank(e.target.value) });
              }}
              error={Boolean(validation.vnbShares) && sharesTouched}
              inputProps={{ min: 0, max: 100, step: "any" }}
            />
            <TextField
              size="small"
              label={VNB_SUPPLY_AGREED_LABEL}
              type="number"
              value={supply.agreedPowerKva ?? ""}
              placeholder={translateText(VNB_OPTIONAL_PLACEHOLDER)}
              onChange={(e) => patchSupply(index, { agreedPowerKva: numberOrBlank(e.target.value) })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: "any", "data-testid": `vnb-supply-agreed-${index}` }}
            />
            {voltage.station && (
              <TextField
                select
                size="small"
                label={VNB_SUPPLY_VOLTAGE_LABEL}
                value={voltage.levels[index] || voltage.station}
                onChange={(e) => patchSupply(index, {
                  voltageLevel: e.target.value === voltage.station ? "" : e.target.value,
                })}
                helperText={voltage.differ ? (
                  <span>
                    {translateText(VNB_REDUCTION_FACTOR_LABEL)}{": "}
                    <span data-no-translate="">{voltage.factors[index].toFixed(3)}</span>
                  </span>
                ) : undefined}
                inputProps={{ "data-testid": `vnb-supply-voltage-${index}` }}
              >
                {VNB_VOLTAGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            )}
            <MaybeTooltip title={VNB_REMOVE_SUPPLY} placement="top" arrow>
              <span>
                <IconButton
                  size="small"
                  aria-label={VNB_REMOVE_SUPPLY}
                  disabled={supplies.length <= 1}
                  onClick={() => removeSupply(index)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </MaybeTooltip>
          </Box>
          {several && (
            <Box sx={{ mt: 1, ml: { xs: 0, sm: 2 } }}>
              <Typography variant="caption" sx={{ display: "block", fontWeight: 600, mb: 0.5 }}>
                {VNB_SUPPLY_FILE_TITLE}
              </Typography>
              <MeteredProfileImport
                compact
                loadProfile={supply.loadProfile || null}
                onLoadProfileChange={(profile) => patchSupply(index, { loadProfile: profile || undefined })}
                cfg={cfg}
              />
            </Box>
          )}
        </Box>
      ))}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {VNB_AGREED_POWER_NOTE}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button size="small" variant="outlined" onClick={addSupply}>
          {VNB_ADD_SUPPLY}
        </Button>
        <Typography variant="body2" color={validation.vnbShares ? "error.main" : "text.secondary"}>
          <span>{VNB_SHARES_TOTAL_LABEL}</span>{": "}
          <span data-no-translate="">{`${Math.round(sharesTotal * 100) / 100}%`}</span>
        </Typography>
      </Box>
      {(validation.vnbShares || validation.vnbSupplies) && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
          {validation.vnbShares || validation.vnbSupplies}
        </Typography>
      )}
      {several && (
        <VirtualNetBillingAllocation
          cfg={cfg}
          supplies={supplies}
          onApply={(percentages) => {
            setSharesTouched(true);
            commitSupplies(supplies.map((s, i) => ({ ...s, sharePct: percentages[i] })));
          }}
        />
      )}
      {fileErrors.map((message) => (
        <Alert severity="error" sx={{ mt: 1.5 }} key={message}>
          {message}
        </Alert>
      ))}

      <Box sx={{ mt: 2 }} data-testid="vnb-voltage">
        <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
          {VNB_VOLTAGE_TITLE}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {VNB_VOLTAGE_TEXT}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5, alignItems: "start" }}>
          <TextField
            select
            size="small"
            label={VNB_STATION_VOLTAGE_LABEL}
            value={voltage.station || ""}
            onChange={(e) => patchVnb({ stationVoltageLevel: e.target.value })}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "vnb-station-voltage" }}
          >
            <MenuItem value="">{VNB_VOLTAGE_SAME_OPTION}</MenuItem>
            {VNB_VOLTAGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          {voltage.differ && (
            <>
              <TextField
                size="small"
                type="number"
                label={VNB_LOSS_MV_LABEL}
                value={vnb.lossUpliftMvPct ?? ""}
                placeholder={String(DEFAULT_LOSS_UPLIFT_MV_PCT)}
                onChange={(e) => patchVnb({ lossUpliftMvPct: numberOrBlank(e.target.value) })}
                error={Boolean(validation.vnbLoss)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0, max: 100, step: "any", "data-testid": "vnb-loss-mv" }}
              />
              <TextField
                size="small"
                type="number"
                label={VNB_LOSS_LV_LABEL}
                value={vnb.lossUpliftLvPct ?? ""}
                placeholder={String(DEFAULT_LOSS_UPLIFT_LV_PCT)}
                onChange={(e) => patchVnb({ lossUpliftLvPct: numberOrBlank(e.target.value) })}
                error={Boolean(validation.vnbLoss)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0, max: 100, step: "any", "data-testid": "vnb-loss-lv" }}
              />
            </>
          )}
        </Box>
        {voltage.differ && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            <span>{VNB_LOSS_HELPER}</span>{" "}<span>{VNB_VOLTAGE_SURPLUS_NOTE}</span>
          </Typography>
        )}
        {validation.vnbLoss && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
            {validation.vnbLoss}
          </Typography>
        )}
      </Box>

      {several && (
        <Box sx={{ mt: 2 }} data-testid="vnb-supplies-sum">
          {sumInUse ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {VNB_SUM_LOCKED_NOTE}
              </Typography>
              <MeteredProfileImport
                locked
                sourceLabel={translateText(VNB_SUM_LABEL)}
                loadProfile={projectLoad}
                onLoadProfileChange={() => {}}
                cfg={cfg}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {VNB_SUM_PENDING_NOTE}
            </Typography>
          )}
        </Box>
      )}
      {!several && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {VNB_SINGLE_SUPPLY_NOTE}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        {voltage.differ ? VNB_ASSUMPTIONS_NOTE_VOLTAGE : VNB_ASSUMPTIONS_NOTE}
      </Typography>
    </Box>
  );
}
